import { useBusinessContext } from '@context'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  PreviewHookProps,
  useDocumentRelationSuggestionPreview,
  useDocumentRelationSuggestions
} from '@query/hooks/useRelationSuggestions'
import {
  createDocumentRelation,
  IDocumentRelation,
  IRelatedDocumentRole,
  IRelationType
} from '@query/documentRelations'
import { IDocument } from '@query/documents'
import { useMemo } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'

import {
  buildOptimisticDocumentRelation,
  getAvailableLinkKinds,
  getDocumentAmountForRelation,
  getPreferredBalanceSign,
  getRelationMapperDisplayAmounts
} from './relationMapperUtils'
import { DocumentRelationContextInput } from './relationContext'
import { DocumentRelationFormValue } from './RelationRow'

const applyRelationNetworkSignalsToCache = (
  queryClient: ReturnType<typeof useQueryClient>,
  slug: string,
  documentId: number,
  signals: IDocumentRelation['source_document_signals']
) => {
  if (!signals) return

  queryClient.setQueryData([slug, 'documents', documentId], (cached: IDocument | undefined) =>
    cached
      ? {
          ...cached,
          relation_network_signals: {
            ...(cached.relation_network_signals ?? {}),
            ...signals
          }
        }
      : cached
  )
}

export type RelationContextForSuggestions = Pick<
  DocumentRelationContextInput,
  | 'sourceDocumentAmount'
  | 'sourceDocumentBalance'
  | 'dueAccountEffect'
  | 'availableLinkKinds'
  | 'accrualPairDueEffect'
  | 'hasAccrualPairDueMismatch'
  | 'accrualPairInvoiceTotal'
  | 'accrualPairPaymentTotal'
>

type UseRelationMapperSuggestionsParams = {
  documentId?: number
  suggestionsEnabled?: boolean
  previewData?: Omit<PreviewHookProps, 'enabled'>
  relations: DocumentRelationFormValue[]
  handleChange: (relations: DocumentRelationFormValue[]) => void
} & RelationContextForSuggestions

export const useRelationMapperSuggestions = ({
  documentId,
  suggestionsEnabled = true,
  previewData,
  relations,
  handleChange,
  sourceDocumentAmount,
  sourceDocumentBalance,
  dueAccountEffect,
  availableLinkKinds: savedAvailableLinkKinds,
  accrualPairDueEffect,
  hasAccrualPairDueMismatch,
  accrualPairInvoiceTotal,
  accrualPairPaymentTotal
}: UseRelationMapperSuggestionsParams) => {
  const { t } = useTranslation()
  const { slug } = useBusinessContext()
  const queryClient = useQueryClient()

  const shouldFetchPreview = Boolean(previewData?.blueprint)
  const shouldUsePreviewSuggestions =
    suggestionsEnabled && relations.length === 0 && shouldFetchPreview
  const shouldFetchSavedDocumentSuggestions =
    suggestionsEnabled && Boolean(documentId) && !shouldUsePreviewSuggestions

  const { data: relationSuggestionsFromDocument = [] } = useDocumentRelationSuggestions({
    documentId: documentId || 0,
    enabled: shouldFetchSavedDocumentSuggestions
  })
  const { data: previewResult } = useDocumentRelationSuggestionPreview({
    date: previewData?.date,
    contactId: previewData?.contactId,
    blueprint: previewData?.blueprint,
    excludeDocumentIds: previewData?.excludeDocumentIds,
    // Only when preview suggestions drive the empty-state UI — never override
    // saved-document amounts/kinds while relations are already linked.
    enabled: shouldUsePreviewSuggestions
  })

  const effectiveDueAccountEffect = shouldUsePreviewSuggestions
    ? previewResult?.relation_entry_signals?.due_account_effect ?? dueAccountEffect
    : dueAccountEffect
  // Match backend preview: prefer source_amount; null falls through to non-zero due, then form amount.
  const effectiveSourceDocumentAmount = shouldUsePreviewSuggestions
    ? previewResult?.source_amount ??
      getDocumentAmountForRelation(
        previewResult != null
          ? { relation_entry_signals: previewResult.relation_entry_signals }
          : null
      ) ??
      sourceDocumentAmount
    : sourceDocumentAmount

  const relationSuggestions = shouldUsePreviewSuggestions
    ? previewResult?.suggestions ?? []
    : relationSuggestionsFromDocument
  const suggestion = relationSuggestions[0] ?? null

  const { sourceAmount, networkInvoiceTotal, networkPaymentTotal, liveOpenAmount, hasMismatch } =
    useMemo(
      () =>
        getRelationMapperDisplayAmounts({
          relations,
          role: relations[0]?.role,
          sourceDocumentAmount: effectiveSourceDocumentAmount,
          dueAccountEffect: effectiveDueAccountEffect,
          accrualPairDueEffect,
          hasAccrualPairDueMismatch,
          accrualPairInvoiceTotal,
          accrualPairPaymentTotal
        }),
      [
        relations,
        effectiveSourceDocumentAmount,
        effectiveDueAccountEffect,
        accrualPairDueEffect,
        hasAccrualPairDueMismatch,
        accrualPairInvoiceTotal,
        accrualPairPaymentTotal
      ]
    )

  const preferredBalanceSign = useMemo(
    () =>
      getPreferredBalanceSign(
        effectiveDueAccountEffect ?? accrualPairDueEffect ?? sourceDocumentBalance
      ),
    [accrualPairDueEffect, effectiveDueAccountEffect, sourceDocumentBalance]
  )

  const availableLinkKindMetas = shouldUsePreviewSuggestions
    ? previewResult?.available_link_kinds ?? savedAvailableLinkKinds ?? null
    : savedAvailableLinkKinds ?? null

  const availableLinkKinds = useMemo(
    () => getAvailableLinkKinds(availableLinkKindMetas),
    [availableLinkKindMetas]
  )

  const createRelationMutation = useMutation<
    IDocumentRelation,
    Error,
    Omit<IDocumentRelation, 'id' | 'description'>
  >({
    mutationFn: payload => {
      if (!documentId) {
        throw new Error('Cannot create relation for unsaved document')
      }
      return createDocumentRelation({ slug, documentId }, payload)
    },
    onSuccess: async relation => {
      if (!documentId) return

      queryClient.setQueryData(
        [slug, 'documents', documentId],
        (cached: IDocument | undefined) => ({
          ...(cached ?? {}),
          relations: [...(cached?.relations ?? []), relation]
        })
      )

      for (const [targetDocumentId, signals] of [
        [documentId, relation.source_document_signals],
        [relation.related_document, relation.related_document_signals]
      ] as const) {
        applyRelationNetworkSignalsToCache(queryClient, slug, targetDocumentId, signals)
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [slug, 'documents', documentId] }),
        queryClient.invalidateQueries({
          queryKey: [slug, 'documents', relation.related_document]
        }),
        queryClient.invalidateQueries({
          queryKey: [slug, 'documents', documentId, 'relation-suggestions']
        })
      ])
    },
    onError: () => {
      toast.error(t('document.form.updateError'))
    }
  })

  const isApplyingSuggestion = createRelationMutation.isPending

  const hasSuggestedRelation =
    suggestion != null &&
    relations.some(relation => relation.related_document === suggestion.related_document)

  const shouldShowSuggestion = suggestion != null && !hasSuggestedRelation

  const handleApplySuggestion = () => {
    if (!suggestion || isApplyingSuggestion) {
      return
    }

    if (shouldUsePreviewSuggestions || !documentId) {
      const cachedDocument = queryClient.getQueryData<IDocument>([
        slug,
        'documents',
        suggestion.related_document
      ])

      const addition = buildOptimisticDocumentRelation(suggestion, { cachedDocument })
      handleChange([...relations, addition])
      return
    }

    createRelationMutation.mutate({
      related_document: suggestion.related_document,
      role: suggestion.role,
      type: suggestion.type
    })
  }

  const suggestionApplyLabel = (() => {
    const isSalary = suggestion?.type === IRelationType.SALARY_PAYMENT
    if (suggestion?.role === IRelatedDocumentRole.ACCRUAL) {
      return t(
        isSalary
          ? 'documentRelations.suggestions.markAsSalaryPayment'
          : 'documentRelations.suggestions.markAsPayment'
      )
    }
    return t(
      isSalary
        ? 'documentRelations.suggestions.markAsSalary'
        : 'documentRelations.suggestions.markAsInvoiceEntry'
    )
  })()

  return {
    invoiceAmount: sourceAmount,
    networkInvoiceTotal,
    networkPaymentTotal,
    liveOpenAmount,
    hasMismatch,
    preferredBalanceSign,
    availableLinkKinds,
    availableLinkKindMetas,
    suggestion,
    shouldShowSuggestion,
    isApplyingSuggestion,
    suggestionApplyLabel,
    handleApplySuggestion
  }
}
